import Mention, { type MentionNodeAttrs } from '@tiptap/extension-mention';
import { mergeAttributes } from '@tiptap/react';
import Fuse, { type FuseOptionKey, type FuseResult } from 'fuse.js';
import { useImmer } from 'use-immer';

export type MentionSuggestion<EntryType> = {
  query?: string;
  items?: FuseResult<EntryType>[];
  command?: (props: MentionNodeAttrs) => void;
};

export interface UseMentionExtensionProps<EntryType> {
  entries: EntryType[];
  keys?: FuseOptionKey<EntryType>[];
}

export function useMentionExtension<EntryType>({ entries, keys }: UseMentionExtensionProps<EntryType>) {
  const [suggestion, setSuggestion] = useImmer<MentionSuggestion<EntryType>>({});

  const fuseFilter = new Fuse(entries, {
    includeScore: true,
    includeMatches: true,
    minMatchCharLength: 0,
    useExtendedSearch: true,
    keys,
  });

  return [
    Mention.configure({
      HTMLAttributes: {
        class: 'input-tag',
      },
      renderText({ options, node }) {
        return `Tag[externalId=${JSON.stringify(node.attrs.id)}]`;
      },
      renderHTML({ options, node }) {
        return [
          'span',
          mergeAttributes({ class: 'input-tag input-tag-type-asset' }, options.HTMLAttributes),
          `${node.attrs.label ?? node.attrs.id}`,
        ];
      },
      suggestion: {
        items({ query }) {
          return fuseFilter.search(query);
        },
        render: () => ({
          onStart({ query, items, command, ...props }) {
            if (!props.clientRect) {
              return;
            }
            setSuggestion((draft) => {
              draft.query = query;
              draft.items = items;
              draft.command = command;
            });
          },
          onUpdate({ query, items, command, ...props }) {
            if (!props.clientRect) {
              return;
            }
            setSuggestion((draft) => {
              draft.query = query;
              draft.items = items;
              draft.command = command;
            });
            return false;
          },
          onKeyDown() {
            return false;
          },
          onExit(_props) {
            setSuggestion({});
          },
        }),
      },
    }),
    suggestion,
  ] as const;
}
